import { Group, Product } from "@/lib/types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  PackageOpen, 
  Plus,
  Edit,
  Trash2
} from "lucide-react";

interface GroupCardProps {
  group: Group;
  onEdit: (group: Group) => void;
  onDelete: (groupId: string) => void;
  onRecordUsage: (groupId: string) => void;
  products: Product[]; // Use products passed from parent component
}

const GroupCard = ({ 
  group, 
  onEdit, 
  onDelete, 
  onRecordUsage,
  products 
}: GroupCardProps) => {
  // Find products that belong to this group
  const groupProducts = products.filter(p => 
    group.productIds.includes(p.id)
  );
  
  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString();
  };

  return (
    <Card className="glass-card overflow-hidden hover:shadow-md transition-shadow duration-200">
      <CardHeader className="pb-2">
        <div className="flex justify-between items-start">
          <Badge variant="outline">
            {groupProducts.length} {groupProducts.length === 1 ? 'Product' : 'Products'}
          </Badge>
          <div className="flex space-x-1">
            <button
              onClick={() => onEdit(group)}
              className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              title="Edit Group"
            >
              <Edit className="h-4 w-4 text-gray-600 dark:text-gray-300" />
            </button>
            <button
              onClick={() => onDelete(group.id)}
              className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              title="Delete Group"
            >
              <Trash2 className="h-4 w-4 text-red-500" />
            </button>
          </div>
        </div>
        <CardTitle className="flex items-center mt-2 text-lg">
          <PackageOpen className="h-5 w-5 mr-2 text-teal-500" />
          {group.name}
        </CardTitle>
      </CardHeader>
      <CardContent className="pb-2">
        {group.description && (
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
            {group.description}
          </p>
        )}
        
        <div className="space-y-2">
          <h4 className="text-sm font-medium">Products in this group:</h4>
          <div className="flex flex-wrap gap-2">
            {groupProducts.length > 0 ? (
              groupProducts.map(product => (
                <Badge key={product.id} variant="secondary" className="text-xs">
                  {product.name}
                </Badge>
              ))
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                No products in this group yet
              </p>
            )}
          </div>
        </div>
      </CardContent>
      <CardFooter className="pt-2 flex justify-between">
        <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
          Created: {formatDate(new Date(group.createdAt))}
        </div>
        <Button 
          onClick={() => onRecordUsage(group.id)}
          size="sm"
          className="btn-primary flex items-center gap-1"
          disabled={groupProducts.length === 0}
        >
          <Plus className="h-4 w-4" />
          Record Usage
        </Button>
      </CardFooter>
    </Card>
  );
};

export default GroupCard;
